import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import retirementAssetsIllustration from "../../../static/illustration--retirement-assets.svg";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

export default function GoalsRetirementAge({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { answer, setAnswer } = useAnswer(screen.id);
  const { user } = useAppStore();
  const [ageError, setAgeError] = useState("");

  const currentAge = user?.currentAge;

  useEffect(() => {
    if (value.length > 0) {
      const retirementAge = parseInt(value);
      const isValid = !isNaN(retirementAge) && retirementAge > currentAge;
      updateValidation(isValid);
      if (!isValid && retirementAge <= currentAge) {
        setAgeError(
          `Retirement age must be greater than your current age (${currentAge})`,
        );
      } else {
        setAgeError("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAge]);

  function updateAnswer(value) {
    setAnswer(value);
    const retirementAge = parseInt(value);

    if (value.length === 0) {
      updateValidation(false);
      setAgeError("");
    } else if (isNaN(retirementAge)) {
      updateValidation(false);
      setAgeError("Please enter a valid age");
    } else if (retirementAge <= currentAge) {
      updateValidation(false);
      setAgeError(
        `Retirement age must be greater than your current age (${currentAge})`,
      );
    } else {
      updateValidation(true);
      setAgeError("");
    }
  }

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:px-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <img
            className="block h-auto mx-auto w-28 md:w-36"
            src={retirementAssetsIllustration}
            alt=""
          />
          <h1 className="mt-6 mb-4 text-3xl md:mb-5 md:text-4xl md:mt-8">
            At what age would you like to retire?
          </h1>
          <div className="max-w-xl mx-auto">
            <p>On average, our clients expect to retire at age 63.</p>
          </div>
        </div>
        <form className="mt-10 md:mt-14">
          <div className="max-w-md mx-auto">
            <h2 className="mb-4 text-xl font-light md:mb-6 md:text-2xl">
              I would like to retire at:
            </h2>
            <label
              className="block mb-1 text-sm font-bold text-left text-gray-800 sr-only"
              htmlFor="goalsRetirementAge"
            >
              Retirement Age
            </label>
            <div className="flex w-48 mx-auto mt-2 rounded shadow-sm">
              <input
                type="text"
                id="goalsRetirementAge"
                name="goalsRetirementAge"
                required
                value={answer}
                onChange={(e) => updateAnswer(e.target.value)}
                onKeyPress={(e) => {
                  const charCode = e.which ? e.which : e.keyCode;
                  if (
                    charCode > 31 &&
                    (charCode < 48 || charCode > 57) &&
                    charCode !== 46
                  ) {
                    e.preventDefault();
                  }
                }}
                aria-label="Retirement Age"
                className={`w-full px-5 py-[1.125rem] mb-0 text-base text-gray-500 border-0 rounded-l ring-1 ring-inset placeholder:text-gray-300 focus:ring-2 focus:ring-inset ${
                  ageError
                    ? "ring-red-500 focus:ring-red-500"
                    : "ring-as-green-100 focus:ring-as-green-50"
                }`}
              />
              <span
                className={`shrink-0 inline-flex items-center rounded-r border border-l-0 px-4 text-gray-600 ${
                  ageError ? "border-red-500" : "border-as-green-100"
                }`}
              >
                years old
              </span>
            </div>
            {ageError && (
              <p className="mt-2 text-sm text-red-600 text-center">
                {ageError}
              </p>
            )}
            {!ageError && currentAge && (
              <p className="mt-2 text-sm text-gray-500 text-center">
                Your current age: {currentAge} years old
              </p>
            )}
            <div className="flex justify-center gap-3 mt-10 md:mt-14 md:gap-5">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={previousScreen}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={Boolean(validation) === false}
                onClick={advanceScreen}
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
